import * as THREE from 'three/webgpu';
import {
    //------ 矩形默认角点构造线 开始------
    createDefaultRectangleOptions,
    //------ 矩形默认角点构造线 结束------
    SketchRectangleCommand,
    type RectangleOptions,
    type RectangleParameters,
} from '../../draw/SketchRectangleDrawingHandler';
import { isSketchCollectionObject } from '../../mesh/guards/isSketchCollectionObject';
import { isSketchLineObject } from '../../mesh/guards/isSketchLineObject';
import {
    SketchCollectionCreationSource,
    SketchCollectionObject,
} from '../../mesh/sketch/SketchCollectionObject';
import type { SketchLineObject } from '../../mesh/sketch/SketchLineObject';
import type { SketchPointObject } from '../../mesh/sketch/SketchPointObject';
import { roundToEightDecimalPlaces } from '../../tool/roundToEightDecimalPlaces';

/**
 * 矩形集合持久化几何数据。
 */
export interface SketchRectangleGeometryData {
    /** 矩形绘制选项快照，用于选中后恢复 PropertyManager 的矩形类型。 */
    rectangle?: RectangleOptions;
    /** 矩形构造线数据。 */
    constructionLines?: {
        /** 构造线对象 ID 列表。 */
        lineIds: string[];
    };
}

/**
 * 矩形角点坐标更新输入。
 */
export interface SketchRectangleCornerCoordinateUpdate {
    /** 角点索引，取值范围为 0-3。 */
    cornerIndex: number;
    /** 局部坐标轴。 */
    axis: 'x' | 'y';
    /** 目标坐标值。 */
    value: number;
}

/**
 * 矩形中心坐标更新输入。
 */
export interface SketchRectangleCenterCoordinateUpdate {
    /** 局部坐标轴。 */
    axis: 'x' | 'y';
    /** 目标坐标值。 */
    value: number;
}

/**
 * 草图矩形属性服务。
 *
 * 设计说明：
 * 1. 矩形在底层由 `SketchCollectionObject` 表达，属性面板不得直接拼接四条线；
 * 2. 本服务统一负责矩形集合识别、参数快照读取与点位写回；
 * 3. web-client 只传递用户输入，草图定义提交与历史记录仍由现有属性编辑服务收口。
 */
export class SketchRectanglePropertyService {
    /** 矩形固定角点数量。 */
    private static readonly RECTANGLE_CORNER_COUNT = 4;

    /**
     * 判断对象是否为矩形集合。
     *
     * @param value 待判断对象。
     * @returns 是矩形集合时返回 true。
     */
    public isRectangleCollection(value: unknown): value is SketchCollectionObject {
        return isSketchCollectionObject(value)
            && value.creationSource === SketchCollectionCreationSource.Rectangle;
    }

    /**
     * 从任意草图对象解析其所属矩形集合。
     *
     * @param object 起始对象。
     * @returns 矩形集合；无法解析时返回 null。
     */
    public resolveRectangleCollectionFromObject(object: THREE.Object3D | null | undefined): SketchCollectionObject | null {
        if (!object) {
            return null;
        }
        if (this.isRectangleCollection(object)) {
            return object;
        }
        if (isSketchLineObject(object) && this.isRectangleCollection(object.closedCollection)) {
            return object.closedCollection;
        }
        return null;
    }

    /**
     * 从对象解析“显式选中”的矩形集合。
     *
     * 设计说明：
     * 1. SolidWorks 中矩形只在刚绘制完成的 PropertyManager 上下文中保持矩形属性；
     * 2. 用户后续单独点选矩形边线或角点时，应回到线/点属性，不能再通过 closedCollection 提升为矩形；
     * 3. 因此该方法只接受集合对象本身，不接收集合内部的边线或端点。
     *
     * @param object 起始对象。
     * @returns 对象本身为矩形集合时返回该集合，否则返回 null。
     */
    public resolveExplicitRectangleCollectionFromObject(
        object: THREE.Object3D | null | undefined,
    ): SketchCollectionObject | null {
        return this.isRectangleCollection(object) ? object : null;
    }

    /**
     * 从当前选中对象集合解析“显式选中”的唯一矩形集合。
     *
     * @param selectedObjects 当前选中对象列表。
     * @returns 仅当唯一选中对象就是矩形集合本身时返回该集合，否则返回 null。
     */
    public resolveExplicitRectangleCollectionFromSelection(
        selectedObjects: readonly THREE.Object3D[],
    ): SketchCollectionObject | null {
        if (selectedObjects.length !== 1) {
            return null;
        }
        return this.resolveExplicitRectangleCollectionFromObject(selectedObjects[0] ?? null);
    }

    /**
     * 从当前选中对象集合解析唯一矩形集合。
     *
     * @param selectedObjects 当前选中对象列表。
     * @returns 所有对象都指向同一矩形时返回该矩形；否则返回 null。
     */
    public resolveRectangleCollectionFromSelection(
        selectedObjects: readonly THREE.Object3D[],
    ): SketchCollectionObject | null {
        if (selectedObjects.length === 0) {
            return null;
        }
        const firstRectangle = this.resolveRectangleCollectionFromObject(selectedObjects[0] ?? null);
        if (!firstRectangle) {
            return null;
        }
        const allPointToSameRectangle = selectedObjects.every((selectedObject) => (
            this.resolveRectangleCollectionFromObject(selectedObject)?.uuid === firstRectangle.uuid
        ));
        return allPointToSameRectangle ? firstRectangle : null;
    }

    /**
     * 读取矩形参数快照。
     *
     * @param rectangle 矩形集合对象。
     * @returns 矩形参数；集合结构不完整时返回 null。
     */
    public snapshotParameters(rectangle: SketchCollectionObject | null | undefined): RectangleParameters | null {
        if (!this.isRectangleCollection(rectangle)) {
            return null;
        }
        const cornerPoints = this.resolveOrderedCornerPoints(rectangle);
        if (!cornerPoints) {
            return null;
        }
        const cornersWorld = cornerPoints.map((point) => point.getPosition());
        const cornersLocal = cornerPoints.map((point) => point.getLocalPosition2D());
        const centerLocal = this.computeCenterLocal(cornersLocal);
        const centerWorld = rectangle.referencePlane.localToWorld(centerLocal);
        return {
            corners: cornersWorld.map((corner) => corner.toArray() as [number, number, number]),
            center: centerWorld.toArray() as [number, number, number],
            corners2d: cornersLocal.map((corner) => [
                roundToEightDecimalPlaces(corner.x),
                roundToEightDecimalPlaces(corner.y),
            ]),
            center2d: [
                roundToEightDecimalPlaces(centerLocal.x),
                roundToEightDecimalPlaces(centerLocal.y),
            ],
        };
    }

    /**
     * 读取矩形绘制选项快照。
     *
     * @param rectangle 矩形集合对象。
     * @param fallback 无几何数据时使用的回退选项。
     * @returns 矩形选项。
     */
    public snapshotOptions(
        rectangle: SketchCollectionObject | null | undefined,
        fallback: RectangleOptions = createDefaultRectangleOptions(),
    ): RectangleOptions {
        if (!this.isRectangleCollection(rectangle)) {
            return {...fallback};
        }
        const geometryData = rectangle.geometryData as SketchRectangleGeometryData | null;
        const storedOptions = geometryData?.rectangle;

        // 显式返回当前受支持字段，防止旧工程中的废弃属性重新进入 PropertyManager 状态。
        return {
            mode: storedOptions?.mode ?? fallback.mode,
            forConstruction: storedOptions?.forConstruction ?? fallback.forConstruction,
            constructionLines: storedOptions?.constructionLines ?? fallback.constructionLines,
        };
    }

    /**
     * 更新矩形角点局部坐标。
     *
     * @param rectangle 矩形集合对象。
     * @param update 更新输入。
     */
    public updateCornerCoordinate(
        rectangle: SketchCollectionObject,
        update: SketchRectangleCornerCoordinateUpdate,
    ): void {
        const cornerPoints = this.requireOrderedCornerPoints(rectangle);
        this.assertValidCornerIndex(update.cornerIndex);
        this.assertFiniteCoordinate(update.value);

        const cornersLocal = cornerPoints.map((point) => point.getLocalPosition2D());
        const nextCornersLocal = cornersLocal.map((corner) => corner.clone());
        const options = this.snapshotOptions(rectangle);

        if (this.isAxisAlignedRectangleMode(options.mode)) {
            this.applyAxisAlignedCornerCoordinate(nextCornersLocal, update);
        } else {
            const targetCorner = nextCornersLocal[update.cornerIndex];
            targetCorner[update.axis] = update.value;
        }

        this.applyCornerLocalPositions(cornerPoints, nextCornersLocal);
        rectangle.refreshContourGeometry();
    }

    /**
     * 更新矩形中心局部坐标。
     *
     * @param rectangle 矩形集合对象。
     * @param update 更新输入。
     */
    public updateCenterCoordinate(
        rectangle: SketchCollectionObject,
        update: SketchRectangleCenterCoordinateUpdate,
    ): void {
        const cornerPoints = this.requireOrderedCornerPoints(rectangle);
        this.assertFiniteCoordinate(update.value);

        const cornersLocal = cornerPoints.map((point) => point.getLocalPosition2D());
        const centerLocal = this.computeCenterLocal(cornersLocal);
        const delta = new THREE.Vector2(0, 0);
        delta[update.axis] = update.value - centerLocal[update.axis];

        const nextCornersLocal = cornersLocal.map((corner) => corner.clone().add(delta));
        this.applyCornerLocalPositions(cornerPoints, nextCornersLocal);
        rectangle.refreshContourGeometry();
    }

    /**
     * 获取矩形集合中的有序外框线段。
     *
     * @param rectangle 矩形集合对象。
     * @returns 有序线段列表；结构无效时返回 null。
     */
    private resolveRectangleLines(rectangle: SketchCollectionObject): SketchLineObject[] | null {
        const lines = rectangle.getLines().filter((source): source is SketchLineObject => isSketchLineObject(source));
        if (lines.length < SketchRectanglePropertyService.RECTANGLE_CORNER_COUNT) {
            return null;
        }
        return lines.slice(0, SketchRectanglePropertyService.RECTANGLE_CORNER_COUNT);
    }

    /**
     * 解析矩形四个有序角点。
     *
     * @param rectangle 矩形集合对象。
     * @returns 有序角点列表；结构无效时返回 null。
     */
    private resolveOrderedCornerPoints(rectangle: SketchCollectionObject): SketchPointObject[] | null {
        const lines = this.resolveRectangleLines(rectangle);
        if (!lines) {
            return null;
        }
        return lines.map((line) => SketchCollectionObject.getSourceEndpoints(line).startPoint);
    }

    /**
     * 解析矩形四个有序角点，失败时抛出明确错误。
     *
     * @param rectangle 矩形集合对象。
     * @returns 有序角点列表。
     */
    private requireOrderedCornerPoints(rectangle: SketchCollectionObject): SketchPointObject[] {
        const cornerPoints = this.resolveOrderedCornerPoints(rectangle);
        if (!cornerPoints) {
            throw new Error('矩形集合外框线段不足，无法编辑矩形参数。');
        }
        return cornerPoints;
    }

    /**
     * 计算矩形中心局部坐标。
     *
     * @param cornersLocal 四个角点局部坐标。
     * @returns 中心局部坐标。
     */
    private computeCenterLocal(cornersLocal: readonly THREE.Vector2[]): THREE.Vector2 {
        const sum = cornersLocal.reduce(
            (accumulator, corner) => accumulator.add(corner),
            new THREE.Vector2(0, 0),
        );
        return sum.multiplyScalar(1 / cornersLocal.length);
    }

    /**
     * 判断是否为轴对齐矩形模式。
     *
     * @param mode 矩形模式。
     * @returns 轴对齐模式返回 true。
     */
    private isAxisAlignedRectangleMode(mode: SketchRectangleCommand): boolean {
        return mode === SketchRectangleCommand.Corner
            || mode === SketchRectangleCommand.Center;
    }

    /**
     * 对轴对齐矩形应用角点坐标变更。
     *
     * 设计说明：
     * 轴对齐矩形的 X/Y 修改实际是在移动对应的竖向或横向边，
     * 因此同侧的另一个角点必须同步移动，不能只改一个端点。
     *
     * @param cornersLocal 待写回的角点局部坐标。
     * @param update 更新输入。
     */
    private applyAxisAlignedCornerCoordinate(
        cornersLocal: THREE.Vector2[],
        update: SketchRectangleCornerCoordinateUpdate,
    ): void {
        const affectedCornerIndexes = update.axis === 'x'
            ? this.resolveAxisAlignedXGroup(update.cornerIndex)
            : this.resolveAxisAlignedYGroup(update.cornerIndex);
        affectedCornerIndexes.forEach((cornerIndex) => {
            cornersLocal[cornerIndex]![update.axis] = update.value;
        });
    }

    /**
     * 解析轴对齐矩形中共享 X 坐标的角点组。
     *
     * @param cornerIndex 当前角点索引。
     * @returns 共享 X 坐标的角点索引。
     */
    private resolveAxisAlignedXGroup(cornerIndex: number): number[] {
        return cornerIndex === 0 || cornerIndex === 3
            ? [0, 3]
            : [1, 2];
    }

    /**
     * 解析轴对齐矩形中共享 Y 坐标的角点组。
     *
     * @param cornerIndex 当前角点索引。
     * @returns 共享 Y 坐标的角点索引。
     */
    private resolveAxisAlignedYGroup(cornerIndex: number): number[] {
        return cornerIndex === 0 || cornerIndex === 1
            ? [0, 1]
            : [2, 3];
    }

    /**
     * 将局部角点坐标写回底层草图点。
     *
     * @param cornerPoints 角点对象列表。
     * @param cornersLocal 目标局部坐标列表。
     */
    private applyCornerLocalPositions(
        cornerPoints: readonly SketchPointObject[],
        cornersLocal: readonly THREE.Vector2[],
    ): void {
        cornerPoints.forEach((cornerPoint, index) => {
            cornerPoint.setLocalPosition2D(cornersLocal[index]!);
        });
    }

    /**
     * 校验角点索引。
     *
     * @param cornerIndex 角点索引。
     */
    private assertValidCornerIndex(cornerIndex: number): void {
        if (
            !Number.isInteger(cornerIndex)
            || cornerIndex < 0
            || cornerIndex >= SketchRectanglePropertyService.RECTANGLE_CORNER_COUNT
        ) {
            throw new Error(`矩形角点索引无效：${cornerIndex}`);
        }
    }

    /**
     * 校验坐标值。
     *
     * @param value 坐标值。
     */
    private assertFiniteCoordinate(value: number): void {
        if (!Number.isFinite(value)) {
            throw new Error(`矩形坐标值无效：${value}`);
        }
    }
}
